import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Row, Col, Container, Alert, Form } from 'react-bootstrap';
import { taskService, departmentService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './MyTasks.css';

const MyTasks = () => {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [departmentsDropdownOpen, setDepartmentsDropdownOpen] = useState(false);

  // Fetch tasks and departments
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch departments
        const departmentsResponse = await departmentService.getAllDepartments();
        const departmentsData = departmentsResponse.data?.data || [];
        setDepartments(departmentsData);
        
        // Fetch tasks
        const tasksResponse = currentUser.role === 'employee'
          ? await taskService.getAssignedTasks()
          : await taskService.getAllTasks();
        
        // Ensure we're working with arrays
        const tasksData = Array.isArray(tasksResponse.data?.data) 
          ? tasksResponse.data.data 
          : [];
          
        setTasks(tasksData);
        setFilteredTasks(tasksData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
        // Set empty arrays on error to prevent filtering issues
        setTasks([]);
        setFilteredTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser.role]);

  // Filter tasks by department
  useEffect(() => {
    if (!Array.isArray(tasks)) {
      setFilteredTasks([]);
      return;
    }

    if (selectedDepartment === 'all') {
      setFilteredTasks(tasks);
    } else {
      const filtered = tasks.filter(task => 
        task.department_id === parseInt(selectedDepartment) || 
        task.department_name === selectedDepartment
      );
      setFilteredTasks(filtered);
    }
  }, [selectedDepartment, tasks]);

  // Handle department selection
  const handleDepartmentSelect = (departmentId) => {
    setSelectedDepartment(departmentId);
    setDepartmentsDropdownOpen(false);
  };

  // Toggle departments dropdown
  const toggleDepartmentsDropdown = () => {
    setDepartmentsDropdownOpen(!departmentsDropdownOpen);
  };

  // Update task status
  const handleUpdateStatus = async (taskId, newStatus) => {
    setActionError(null);
    setActionLoading(true);
    try {
      await taskService.updateTaskStatus(taskId, { status: newStatus });
      // Refresh tasks after update
      const response = currentUser.role === 'employee'
        ? await taskService.getAssignedTasks()
        : await taskService.getAllTasks();
      const tasksData = response.data?.data || [];
      setTasks(tasksData);
      
      // Show success message
      setSuccess(`Task status updated to ${newStatus.replace('_', ' ')}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating task status:', err);
      setActionError('Failed to update task status');
    } finally {
      setActionLoading(false);
    }
  };

  // Request task completion approval
  const handleRequestApproval = async (taskId) => {
    setActionError(null);
    setActionLoading(true);
    try {
      // First update UI optimistically
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'awaiting_approval' }
            : task
        )
      );
      
      // Then make the API call
      await taskService.requestTaskCompletion(taskId);
      
      // Show success message
      setSuccess('Task completion request submitted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error requesting approval:', err);
      // Revert the optimistic update if the API call failed
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'in_progress' }
            : task
        )
      );
      setActionError('Failed to request approval. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Approve task completion
  const handleApproveTask = async (taskId) => {
    setActionError(null);
    setActionLoading(true);
    try {
      await taskService.approveTask(taskId);
      // Refresh tasks after approval
      const response = currentUser.role === 'employee'
        ? await taskService.getAssignedTasks()
        : await taskService.getAllTasks();
      const tasksData = response.data?.data || [];
      setTasks(tasksData);
      
      // Show success message
      setSuccess('Task has been approved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error approving task:', err);
      setActionError('Failed to approve task');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'todo': return 'secondary';
      case 'in_progress': return 'primary';
      case 'awaiting_approval': return 'warning';
      case 'completed': return 'success';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      case 'urgent': return 'danger';
      default: return 'secondary';
    }
  };

  // Filter tasks by status
  const todoTasks = Array.isArray(filteredTasks) ? filteredTasks.filter(task => 
    task.status === 'todo' || 
    task.status === 'pending' || 
    task.status === ''
  ) : [];
  
  const inProgressTasks = Array.isArray(filteredTasks) ? filteredTasks.filter(task => 
    task.status === 'in_progress' || task.status === 'awaiting_approval'
  ) : [];
  
  const completedTasks = Array.isArray(filteredTasks) ? filteredTasks.filter(task => 
    task.status === 'completed'
  ) : [];

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'todo': return 'fa-circle-exclamation';
      case 'in_progress': return 'fa-spinner fa-spin';
      case 'awaiting_approval': return 'fa-clock';
      case 'completed': return 'fa-circle-check';
      default: return 'fa-circle-question';
    }
  };

  // Get priority icon
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'low': return 'fa-arrow-down';
      case 'medium': return 'fa-equals';
      case 'high': return 'fa-arrow-up';
      case 'urgent': return 'fa-bolt';
      default: return 'fa-minus';
    }
  };
  
  // Get employee initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  // Render task card
  const TaskCard = ({ task }) => (
    <Card className="task-card mb-3">
      <Card.Body>
        <div className="task-header">
          <h5 className="task-title">{task.title}</h5>
          <Badge bg={getStatusBadgeVariant(task.status)}>
            {task.status === 'awaiting_approval' ? 'Waiting for Approval' :
             task.status === 'in_progress' ? 'In Progress' :
             task.status === 'completed' ? 'Completed' :
             task.status === 'pending' ? 'To Do' :
             task.status}
          </Badge>
        </div>
        
        <p className="task-description">{task.description}</p>
        
        <div className="task-meta">
          <div>
            <i className="fas fa-calendar me-2"></i>
            Due: {formatDate(task.due_date)}
          </div>
          <div>
            <i className="fas fa-flag me-2"></i>
            Priority: <Badge bg={getPriorityBadgeVariant(task.priority)}>{task.priority}</Badge>
          </div>
        </div>
        
        <div className="task-employee">
          <div className="employee-avatar">
            {getInitials(task.assigned_to || task.assigned_to_username || '')}
          </div>
          <span className="employee-name">
            {task.assigned_to || task.assigned_to_username || 'Unassigned'}
          </span>
        </div>
        
        <div className="task-actions">
          {currentUser.role === 'employee' && (task.status === 'todo' || task.status === 'pending' || task.status === '') && (
            <Button 
              variant="primary" 
              size="sm" 
              className="action-button"
              onClick={() => handleUpdateStatus(task.id, 'in_progress')}
              disabled={actionLoading}
            >
              <i className="fas fa-play me-1"></i> Start Task
            </Button>
          )}
          {currentUser.role === 'employee' && task.status === 'in_progress' && (
            <Button 
              variant="warning" 
              size="sm" 
              className="action-button"
              onClick={() => handleRequestApproval(task.id)}
              disabled={actionLoading}
            >
              <i className="fas fa-check-circle me-1"></i> Request Approval
            </Button>
          )}
          {currentUser.role === 'employee' && task.status === 'awaiting_approval' && (
            <div className="text-muted">
              <i className="fas fa-clock me-1"></i> Waiting for Approval
            </div>
          )}
          {(currentUser.role === 'admin' || currentUser.role === 'manager') && 
           task.status === 'awaiting_approval' && (
            <Button 
              variant="success" 
              size="sm" 
              className="action-button"
              onClick={() => handleApproveTask(task.id)}
              disabled={actionLoading}
            >
              <i className="fas fa-thumbs-up me-1"></i> Approve
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) return <div className="loading-container"><i className="fas fa-spinner fa-spin"></i> Loading tasks...</div>;
  if (error) return <div className="error-container"><i className="fas fa-exclamation-triangle"></i> {error}</div>;

  return (
    <Container fluid className="my-tasks-container py-4">
      <div className="tasks-header mb-4">
        <h2 className="page-title">
          <i className="fas fa-clipboard-list me-2"></i>
          My Tasks
        </h2>
      </div>
      
      {actionError && <Alert variant="danger" className="mb-4">{actionError}</Alert>}
      {success && <Alert variant="success" className="mb-4">{success}</Alert>}
      
      <div className="mb-4">
        <div className="filter-section">
          <div className="filter-label">Filter by Department</div>
          <div className="custom-dropdown">
            <button 
              className="dropdown-toggle-btn" 
              onClick={toggleDepartmentsDropdown}
            >
              {selectedDepartment === 'all' 
                ? 'All Departments' 
                : departments.find(d => d.id === parseInt(selectedDepartment) || d.name === selectedDepartment)?.name || 'All Departments'
              }
              <i className={`fas fa-chevron-${departmentsDropdownOpen ? 'up' : 'down'} ms-2`}></i>
            </button>
            
            {departmentsDropdownOpen && (
              <div className="dropdown-menu show">
                <div 
                  className={`dropdown-item ${selectedDepartment === 'all' ? 'active' : ''}`}
                  onClick={() => handleDepartmentSelect('all')}
                >
                  All Departments
                </div>
                {departments.map(department => (
                  <div 
                    key={department.id} 
                    className={`dropdown-item ${selectedDepartment === department.id.toString() ? 'active' : ''}`}
                    onClick={() => handleDepartmentSelect(department.id.toString())}
                  >
                    {department.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Row className="task-board">
        <Col md={4}>
          <div className="task-column todo-column">
            <div className="column-header">
              <div className="column-icon">
                <i className="fas fa-clipboard-list"></i>
              </div>
              <h3 className="column-title">To Do</h3>
              <Badge bg="secondary" pill className="task-count">{todoTasks.length}</Badge>
            </div>
            <div className="task-list">
              {todoTasks.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-clipboard"></i>
                  <p>No tasks to do</p>
                </div>
              ) : (
                todoTasks.map(task => <TaskCard key={task.id} task={task} />)
              )}
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="task-column progress-column">
            <div className="column-header">
              <div className="column-icon">
                <i className="fas fa-spinner"></i>
              </div>
              <h3 className="column-title">In Progress</h3>
              <Badge bg="primary" pill className="task-count">{inProgressTasks.length}</Badge>
            </div>
            <div className="task-list">
              {inProgressTasks.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-hourglass-half"></i>
                  <p>No tasks in progress</p>
                </div>
              ) : (
                inProgressTasks.map(task => <TaskCard key={task.id} task={task} />)
              )}
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className="task-column completed-column">
            <div className="column-header">
              <div className="column-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="column-title">Completed</h3>
              <Badge bg="success" pill className="task-count">{completedTasks.length}</Badge>
            </div>
            <div className="task-list">
              {completedTasks.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-trophy"></i>
                  <p>No completed tasks</p>
                </div>
              ) : (
                completedTasks.map(task => <TaskCard key={task.id} task={task} />)
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MyTasks;
